import { Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { useCallback, useRef } from 'react';
import {
  ButtonContainer,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import validationSchema from './validations';
import schema from './edit.json';
import {
  preProcessResponseData,
  fetchClaimsSetting,
  fetchRateCard,
  updateClaimsSettingApi,
  updateRateCard,
} from './lib';

const FORM_ID = 'ServiceProviderClaimsSettingForm';
const PERMISSION_SP = 'SERVICEPROVIDERPROFILE';

function ClaimsSetting({ serviceProviderId, onChange = () => undefined }) {
  const formRef = useRef();
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get, put } = useDataProvider(provider);

  const claimApiUrl = `${getApiUrl()}/sxcl/v1/service-provider-profile/${serviceProviderId}/claims`;
  const rateCardUrl = `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}`;

  const commonParams = {
    claimApiUrl,
    rateCardUrl,
    get,
    put,
  };

  const fetchDefaultValues = useCallback(async () => {
    const [claimData, rateCardData] = await Promise.all([
      fetchClaimsSetting(commonParams),
      fetchRateCard(commonParams),
    ]);
    const initialValuesFound = preProcessResponseData(claimData, rateCardData);
    const result = {
      data: initialValuesFound,
      errors: null,
    };

    return result;
  }, [serviceProviderId]);

  const onSubmit = async data => {
    let errors = [];
    let claimData = null;
    let rateCardData = null;

    const [claimResponse, rateCardResponse] = await Promise.all([
      updateClaimsSettingApi(commonParams, data),
      updateRateCard(commonParams, data),
    ]);

    if (claimResponse?.ok) {
      claimData = claimResponse?.data;
    } else {
      errors = [...(claimResponse?.errors || [])];
    }

    if (rateCardResponse?.ok) {
      rateCardData = rateCardResponse?.data;
    } else {
      errors = [...(rateCardResponse?.errors || [])];
    }

    if (errors.length > 0) {
      return {
        nextValues: data,
        errors,
      };
    }
    snackbar.showMessage({
      type: 'success',
      data: formatMessage({
        id: 'SERVICEPROVIDER_SYSTEMSETTINGS_CLAIM_FETCH_SUCCESS',
        defaultMessage: 'Claim System settings were saved successfully',
      }),
    });
    const processedValues = preProcessResponseData(claimData, rateCardData);
    return {
      nextValues: processedValues,
      errors: null,
      callback: () => {
        onChange();
      },
    };
  };

  return (
    <PageSection>
      <Box sx={{ mt: 2 }}>
        <Form
          formId={FORM_ID}
          ref={formRef}
          permissionEntities={[PERMISSION_SP]}
          onSubmit={onSubmit}
          schema={schema}
          fetchDefaultValues={fetchDefaultValues}
          validationSchema={validationSchema(formatMessage)}>
          <ButtonContainer>
            <PermissionButton
              permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_CLAIMS"
              variant="contained"
              startIcon={<IconLibrary.Save />}
              color="primary"
              onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
              {formatMessage({
                id: 'CLAIMS_SETTING_SAVE_BUTTON',
                defaultMessage: 'Save Claim Settings',
              })}
            </PermissionButton>
            <PermissionButton
              permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_CLAIMS"
              variant="contained"
              startIcon={<IconLibrary.Reset />}
              color="primary"
              onClick={() => formRef.current?.reset()}>
              {formatMessage({
                id: 'CLAIMS_SETTING_RESET_BUTTON',
                defaultMessage: 'Reset',
              })}
            </PermissionButton>
          </ButtonContainer>
        </Form>
      </Box>
    </PageSection>
  );
}

export default ClaimsSetting;
